/**
 * Phone validation utilities based on Valid_num database table
 */
import React from 'react';
import { apiBaseUrl } from '../config/api_config';

let validPhoneConfigs = [];
let configsLoaded = false;

/**
 * Fetch valid phone number configurations from the server
 */
export const loadPhoneConfigs = async () => {
    try {
        const response = await fetch(`${apiBaseUrl}/haseela_system_admin/valid-phone-numbers/`);
        
        if (!response.ok) {
            throw new Error('Failed to fetch phone configurations');
        }
        
        const data = await response.json();
        validPhoneConfigs = data;
        configsLoaded = true;
        
        return validPhoneConfigs;
    } catch (error) {
        console.error('Error loading phone configurations:', error);
        // Fallback to default configuration if API fails
        validPhoneConfigs = [
            { phone_prefix: '+972', phone_suffix_len: '9', country: 'Palestine' },
            { phone_prefix: '+970', phone_suffix_len: '9', country: 'Palestine' }
        ];
        configsLoaded = true;
        return validPhoneConfigs;
    }
};

/**
 * Get cached phone configurations or load them if not loaded
 */
export const getPhoneConfigs = async () => {
    if (!configsLoaded) {
        await loadPhoneConfigs();
    }
    return validPhoneConfigs;
};

/**
 * Validate and normalize a phone number based on Valid_num configurations
 * @param {string} phoneNum - The phone number to validate
 * @returns {object} - { isValid: boolean, normalizedPhone: string, error: string }
 */
export const validatePhoneNumber = async (phoneNum) => {
    const configs = await getPhoneConfigs();
    
    if (!configs || configs.length === 0) {
        return {
            isValid: false,
            normalizedPhone: phoneNum,
            error: 'No valid phone number configurations available'
        };
    }
    
    let phone = phoneNum.trim();
    
    // If phone doesn't start with +, try to add it
    if (!phone.startsWith('+')) {
        let matched = false;
        for (const config of configs) {
            const prefix = config.phone_prefix.replace('+', '');
            if (phone.startsWith(prefix)) {
                phone = '+' + phone;
                matched = true;
                break;
            }
        }
        
        // Check if it's just digits (9 digits for Palestinian numbers)
        if (!matched && /^\d+$/.test(phone)) {
            // Try to find a default prefix (first one in the list)
            const suffixLength = parseInt(configs[0].phone_suffix_len);
            if (phone.length === suffixLength) {
                phone = configs[0].phone_prefix + phone;
                matched = true;
            }
        }
        
        if (!matched) {
            const validPrefixes = configs.map(c => c.phone_prefix).join(', ');
            return {
                isValid: false,
                normalizedPhone: phoneNum,
                error: `Phone number must start with one of: ${validPrefixes}`
            };
        }
    }
    
    // Validate format with + prefix
    let isValid = false;
    let matchedConfig = null;
    let selectedPrefix = null;
    
    // First, try to find which prefix the user selected
    for (const config of configs) {
        if (phone.startsWith(config.phone_prefix)) {
            selectedPrefix = config;
            const digitsAfterPrefix = phone.substring(config.phone_prefix.length);
            const expectedLength = parseInt(config.phone_suffix_len);
            
            if (/^\d+$/.test(digitsAfterPrefix) && digitsAfterPrefix.length === expectedLength) {
                isValid = true;
                matchedConfig = config;
                break;
            }
            // We found the prefix but length is wrong, break here to give specific error
            break;
        }
    }
    
    if (!isValid) {
        // If we found which prefix was selected, give specific error for that prefix
        if (selectedPrefix) {
            const digitsAfterPrefix = phone.substring(selectedPrefix.phone_prefix.length);
            const actualLength = digitsAfterPrefix.length;
            const expectedLength = parseInt(selectedPrefix.phone_suffix_len);
            
            return {
                isValid: false,
                normalizedPhone: phone,
                error: `Phone number must be ${selectedPrefix.phone_prefix} followed by exactly ${expectedLength} digits (you entered ${actualLength} digits)`
            };
        }
        
        // If no prefix matched, show available options
        const validPrefixes = configs.map(c => c.phone_prefix).join(', ');
        return {
            isValid: false,
            normalizedPhone: phone,
            error: `Phone number must start with one of these country codes: ${validPrefixes}`
        };
    }
    
    return {
        isValid: true,
        normalizedPhone: phone,
        error: null,
        country: matchedConfig ? matchedConfig.country : null
    };
};

/**
 * Get a user-friendly input placeholder based on configurations
 * @returns {string} - Placeholder text for phone input
 */
export const getPhonePlaceholder = async () => {
    const configs = await getPhoneConfigs();
    
    if (!configs || configs.length === 0) {
        return 'Enter phone number';
    }
    
    // Return first config as example
    const firstConfig = configs[0];
    const exampleDigits = '0'.repeat(parseInt(firstConfig.phone_suffix_len));
    
    return `${firstConfig.phone_prefix}${exampleDigits}`;
};

/**
 * Get all valid phone prefixes for dropdown selection
 * @returns {Array} - Array of {prefix, country, length} objects
 */
export const getPhonePrefixes = async () => {
    const configs = await getPhoneConfigs();
    return configs.map(config => ({
        prefix: config.phone_prefix,
        country: config.country,
        length: parseInt(config.phone_suffix_len)
    }));
};

/**
 * React hook to get phone prefixes
 * @returns {Array} - Array of prefix configurations
 */
export const usePhonePrefixes = () => {
    const [prefixes, setPrefixes] = React.useState([]);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        getPhonePrefixes().then(data => {
            setPrefixes(data);
            setLoading(false);
        }).catch(err => {
            console.error('Error loading prefixes:', err);
            setLoading(false);
        });
    }, []);

    return { prefixes, loading };
};

